import { yupResolver } from '@hookform/resolvers/yup';
import { data as allCurrencies } from 'currency-codes';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { number, object, string } from 'yup';

import { isEmpty } from 'lodash-es';
import { BABYLON_MIN_SERVICE_DURATION_MINUTES } from '../../constants/Constants';
import Input from './Input';

export type UserServiceType = {
  name: string;
  duration: number;
  currency: string;
  price: number;
  valid?: boolean;
};

export type CurrencyType = {
  id: string;
  label: string;
};

interface AddServiceProps {
  service: UserServiceType | undefined;
  currency?: string;
  onChange: (service: UserServiceType) => void;
  onSubmit: boolean;
}

const formattedCurrencies: CurrencyType[] = allCurrencies.map((cc: any) => ({
  id: cc.code,
  label: cc.code,
}));

const AddService: React.FC<AddServiceProps> = ({
  service,
  onChange,
  currency,
  onSubmit,
}) => {
  const { t } = useTranslation();

  const formSchema = object().shape({
    name: string()
      .required(t('static.join.validation_step_service_error_name_required'))
      .min(2, t('static.join.validation_step_service_error_name_short'))
      .max(50, t('static.join.validation_step_service_error_name_long')),
    duration: number()
      .required(
        t('static.join.validation_step_service_error_duration_required'),
      )
      .typeError(t('static.join.validation_step_service_error_duration_number'))
      .min(
        BABYLON_MIN_SERVICE_DURATION_MINUTES,
        t('static.join.validation_step_service_error_duration_minimum', {
          minutes: BABYLON_MIN_SERVICE_DURATION_MINUTES,
        }),
      ),
    price: number()
      .required(t('static.join.validation_step_service_error_price_required'))
      .typeError(t('static.join.validation_step_service_error_price_number'))
      .min(0, t('static.join.validation_step_service_error_price_positive')),
  });

  const {
    register,
    trigger,
    formState: { errors },
    getValues,
    watch,
  } = useForm({
    resolver: yupResolver(formSchema),
    defaultValues: {
      name:
        service?.name || t('static.join.modal_step_service_placeholder_name'),
      duration: service?.duration || 60,
      price: service?.price || 0,
    },
    mode: 'onChange',
  });

  const inputCurrency = useMemo<CurrencyType[]>(
    () => formattedCurrencies.filter((fc) => fc.label === currency),
    [currency],
  );

  const [selectedCurrency, setSelectedCurrency] = useState<
    CurrencyType | undefined
  >(inputCurrency.length > 0 ? inputCurrency[0] : undefined);

  useEffect(() => {
    trigger()
      .then((success) => {
        if (success) {
          const newService: UserServiceType = {
            ...getValues(),
            currency: selectedCurrency?.label ?? 'USD',
            valid: isEmpty(errors),
          };
          onChange(newService);
        }
      })
      .catch((e: any) => console.error(e));
  }, [onSubmit]);

  useEffect(() => {
    watch(() => {
      trigger();
    });
  }, [watch]);

  useEffect(() => {
    const newService: UserServiceType = {
      ...getValues(),
      currency: selectedCurrency?.label ?? 'USD',
      valid: isEmpty(errors),
    };
    onChange(newService);
  }, [errors]);

  const createSelectLabel = (option: any) => (
    <div className="flex flex-row items-center gap-3">
      <div>{option.label}</div>
    </div>
  );

  return (
    <div>
      <div className="my-4">
        <Input
          id="name"
          label={t('static.join.modal_step_service_label_name')}
          disabled={false}
          register={register}
          errors={errors}
        />
      </div>
      <div className="my-4">
        <Input
          id="duration"
          label={t('static.join.modal_step_service_label_duration')}
          disabled={false}
          register={register}
          errors={errors}
          type="number"
        />
      </div>
      <div className="flex flex-row">
        <div className="grow">
          <Input
            id="price"
            label={t('static.join.modal_step_service_label_price')}
            disabled={false}
            register={register}
            errors={errors}
            type="number"
          />
        </div>
        <div className="ml-2 w-40">
          <Select
            data-test-id="select-currency"
            placeholder={currency}
            isSearchable={false}
            menuPlacement="top"
            menuPosition="fixed"
            menuShouldScrollIntoView
            closeMenuOnSelect
            className="rounded-md border-2"
            options={formattedCurrencies}
            value={selectedCurrency}
            onChange={(newCurrency) => {
              if (newCurrency) {
                setSelectedCurrency(newCurrency);
              } else {
                setSelectedCurrency(
                  inputCurrency.length > 0 ? inputCurrency[0] : undefined,
                );
              }
            }}
            formatOptionLabel={createSelectLabel}
            classNames={{
              control: () => 'px-3 py-1 border-2',
              input: () => 'text-lg',
              option: () => 'text-lg',
            }}
            theme={(theme) => ({
              ...theme,
              borderRadius: 6,
              colors: {
                ...theme.colors,
                primary: '#083344',
                primary25: '#ecfeff',
              },
            })}
          />
        </div>
      </div>
    </div>
  );
};

export default AddService;
